import { useState } from 'react'

import { type DropdownItem } from '@factorialco/f0-react/dist/experimental'
import { IntegrationsSyncRunSyncingDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { aggregateStatuses } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { statusesItems } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/statusesItems'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export type UseSyncRunSyncingReturn = {
  isLoading: boolean
  syncingLabel: string
  syncingCounter: string
  dropdownItems: DropdownItem[]
}

export const useSyncRunSyncing = (): UseSyncRunSyncingReturn => {
  const { isSyncRunCreating, lastSyncRun, configuration, capabilityMode } =
    usePrivateIntegrationsSyncContext()
  const isExportMode = capabilityMode === 'file'
  const [lastFetchedCursor, setLastFetchedCursor] = useState<string | null>(null)
  const [
    fetchSyncRunSyncing,
    { data: syncRunSyncingData, loading: syncRunSyncingLoading, fetchMore },
  ] = useLazyQuery(IntegrationsSyncRunSyncingDocument)

  const lastSyncRunId = lastSyncRun?.id
  const includeSyncable = !!configuration.syncableStatesAggregation
  const pageInfo = syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.pageInfo
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const endCursor = pageInfo?.endCursor

  const fetchMoreCallback = useCallback(() => {
    if (lastFetchedCursor === endCursor) return
    if (!endCursor) return

    setLastFetchedCursor(endCursor)

    fetchMore({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
        after: endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.integrations?.syncableSyncRunsConnection) {
          return previousResult
        }

        const previousNodes = previousResult.integrations?.syncableSyncRunsConnection?.nodes ?? []
        const newNodes = fetchMoreResult.integrations.syncableSyncRunsConnection.nodes ?? []

        return {
          ...previousResult,
          integrations: {
            ...previousResult.integrations,
            syncableSyncRunsConnection: {
              ...fetchMoreResult.integrations.syncableSyncRunsConnection,
              nodes: [...previousNodes, ...newNodes],
            },
          },
        }
      },
    })
  }, [endCursor, lastSyncRunId, includeSyncable, lastFetchedCursor])

  useUnsafeEffect(() => {
    if (isSyncRunCreating || !lastSyncRunId) return

    fetchSyncRunSyncing({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
        syncableType: configuration.syncableType,
      },
    })
  }, [
    isSyncRunCreating,
    lastSyncRunId,
    includeSyncable,
    configuration.syncableType,
    fetchSyncRunSyncing,
  ])

  if (hasNextPage && lastFetchedCursor !== endCursor) {
    fetchMoreCallback()
  }

  const isLoading = isSyncRunCreating || syncRunSyncingLoading || hasNextPage
  const syncingLabel = isExportMode
    ? i18n.t('integrations.exportable_status.exporting')
    : i18n.t('integrations.syncable_status.syncing')

  const syncableStates = aggregateStatuses(
    syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.nodes.map(
      (node) => node.syncableState
    ) ?? [],
    configuration.syncableStatesAggregation
  )

  const syncingCounter = `${
    syncableStates.filter((status) => !status.has('syncing')).length
  } / ${syncableStates.length}`

  const dropdownItems = statusesItems(syncableStates, isExportMode)

  return {
    isLoading,
    syncingLabel,
    syncingCounter,
    dropdownItems,
  }
}
